import { Currency } from '@ps/web-core-enums';
import type {
  CreateClubTournamentResponse,
  HGCreateClubTourn3,
  HGCreateClubTournamentReply
} from '@ps/web-shared-api-types/v0/SwHomeGames';
import { useCallback, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { lastValueFrom } from 'rxjs';

import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store/types';
import { ENDPOINTS, handleServerResponse } from '@swf/poker';

import { HGOnboarding, ModalRoutes } from '../../../types/common';
import CreateTournamentContext from '../CreateTournamentContext/CreateTournamentContext';
import { getRecurringOptions } from '../Recurrence/utils/utils';
import type { FormValues } from '../utils/common';
import getZonedTime, {
  getCustomTimezoneOffsetById,
  getTimezoneNameById,
  normalizeDateValue
} from '../utils/getZonedTime';

import { TXTSRV_SorryX_we_are_experiencing_tem_ELL } from './useCreateClubTournament.messages.json';
import useGetDefaultName from './useGetDefaultName';

const useCreateClubTournament = (quickFlow?: boolean) => {
  const request = useRequest();
  const { formatMessage } = useIntl();
  const { modal } = useDispatch<Dispatch>();
  const { state, setState } = useContext(CreateTournamentContext);
  const { clubId, widgetId, widgetDispatch, widgetSelect, noGames } = state;
  const getDefaultName = useGetDefaultName();
  const accTimezoneNum = useSelector(select.account.getTimezone);
  const accTimezone = useMemo(() => getTimezoneNameById(accTimezoneNum), [accTimezoneNum]);
  const accTimezoneOffset = useMemo(
    () => getCustomTimezoneOffsetById(accTimezoneNum),
    [accTimezoneNum]
  );
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const structures = useSelector(widgetSelect!.tournamentStructures);

  return {
    createClubTournament: useCallback(
      // TODO: replace with types
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      async (formProps: any) => {
        const values: FormValues = quickFlow ? formProps : formProps.values;
        const { tournFastDealing, tournKnockoutPrize, tournMaxPlayers } = quickFlow
          ? structures
          : formProps.data.tournamentStructuresResponse.data;

        setState((prevState) => ({ ...prevState, loading: true }));

        const {
          gameName,
          startDate,
          startTime,
          handType,
          potStruct,
          tableSize,
          currency,
          buyIn,
          startingChips,
          payout,
          tournFormat,
          tournLateRegLevel,
          levelLength,
          tournReentry,
          tournSyncBreaks,
          repeat,
          enableVideoChat,
          countTowardsClubStandings
        }: FormValues = values;
        const date = new Date(normalizeDateValue(startDate)); // useField returns startDate in such format `yyyy-DD-mm` which is not a proper format to pass to new Date so we should normalize it
        const year = date.getUTCFullYear();
        const month = (date.getMonth() + 1).toString();
        const errorMessage = formatMessage(TXTSRV_SorryX_we_are_experiencing_tem_ELL);
        const [day, hour, minute] = getZonedTime(date, startTime, accTimezoneOffset, accTimezone);
        const recurringTournament = repeat ? getRecurringOptions(values) : undefined;
        const handTypeValues = JSON.parse(handType as string);
        const buyInValues = JSON.parse(buyIn as string);
        const name = gameName || getDefaultName(handType);
        const formattedName = repeat ? `${gameName || getDefaultName(handType)}` : name;

        const response = await lastValueFrom(
          request<CreateClubTournamentResponse>({
            service: ENDPOINTS.createClubTournament,
            body: {
              createClubTournament: {
                createTournParams2: {
                  createTournParams: {
                    clubId,
                    gameName: formattedName,
                    countTowardsClubStandings,
                    hiLo: handTypeValues.isHiLo,
                    tableSize,
                    tournamentLobbyText: '',
                    isFastDealing: tournFastDealing[0],
                    currency,
                    handType: handTypeValues.handType,
                    potStruct,
                    buyIn: buyInValues.buyIn,
                    payout,
                    startingChips,
                    tournFormat,
                    levelLength,
                    tournKnockoutPrize,
                    tournMaxPlayers,
                    scalePM: buyInValues.scalePM,
                    syncBreaksOn: tournSyncBreaks,
                    playMoney: currency === Currency.PlayMoney,
                    year,
                    month,
                    day,
                    hour,
                    minute
                  },
                  enableVideoChat,
                  lateRegLevels: tournLateRegLevel,
                  reentry: tournReentry
                },
                recurringTournament
              }
            } as HGCreateClubTourn3,
            http: true,
            method: 'PUT'
          })
        ).catch((e) => {
          setState((prevState) => ({
            ...prevState,
            error: e.body?.message || errorMessage
          }));

          return Promise.reject();
        });

        setState((prevState) => ({ ...prevState, loading: false }));

        if (!response || !response.body) {
          setState((prevState) => ({
            ...prevState,
            error: errorMessage
          }));

          return Promise.reject();
        }

        const createClubTournamentResponse = handleServerResponse<
          CreateClubTournamentResponse,
          HGCreateClubTournamentReply
        >({
          response: response.body,
          accessor: 'createClubTournamentReply'
        });
        if (!createClubTournamentResponse.error) {
          if (noGames) {
            widgetDispatch?.setLocalStorageItem({
              onboarding: {
                [HGOnboarding.CreatedGame]: true
              }
            });
          }
          modal.replace([
            { path: ModalRoutes.createTournamentSuccess, locationState: { widgetId } }
          ]);

          window.starsweb?.homegames?.refresh?.();

          return { createClubTournamentResponse };
        }

        setState((prevState) => ({ ...prevState, error: `${createClubTournamentResponse.error}` }));

        return Promise.reject();
      },
      [
        quickFlow,
        structures,
        setState,
        formatMessage,
        accTimezoneOffset,
        accTimezone,
        getDefaultName,
        request,
        clubId,
        noGames,
        modal,
        widgetId,
        widgetDispatch
      ]
    )
  };
};

export default useCreateClubTournament;
